import React, { useState, useMemo } from "react";
import {
  X,
  Users,
  Plus,
  Minus,
  AlertCircle,
  Check,
  Search,
} from "lucide-react";
import { ROUTINES_API, fetchWithAuth } from "../../utils/api";

const BulkDancerModal = ({
  isOpen,
  onClose,
  mode, // "add" or "remove"
  selectedRoutines,
  roster,
  isDarkMode,
  onSuccess,
}) => {
  const [selectedDancers, setSelectedDancers] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Filter roster to only show dancers
  const availableDancers = useMemo(() => {
    return roster.filter(member => {
      const roles = member.role || [];
      const normalizedRoles = Array.isArray(roles) 
        ? roles.map(r => typeof r === 'string' ? r : r.S)
        : [roles];
      
      if (!normalizedRoles.includes("Dancer")) return false;
      
      if (searchQuery.trim()) {
        const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase().trim());
      }
      
      return true;
    });
  }, [roster, searchQuery]);

  // Get dancers that are already in ALL selected routines (for remove mode)
  const dancersInAllRoutines = useMemo(() => {
    if (mode !== "remove" || selectedRoutines.length === 0) return new Set();
    
    const routineDancerSets = selectedRoutines.map(routine => 
      new Set(routine.dancers || [])
    );
    
    // Find dancers that appear in ALL selected routines
    const commonDancers = new Set();
    const firstRoutineDancers = routineDancerSets[0];
    
    firstRoutineDancers.forEach(dancerId => {
      if (routineDancerSets.every(set => set.has(dancerId))) {
        commonDancers.add(dancerId);
      }
    });
    
    return commonDancers;
  }, [mode, selectedRoutines]);

  // Filter available dancers based on mode
  const filteredDancers = useMemo(() => {
    if (mode === "add") {
      // For add mode, show all dancers
      return availableDancers;
    } else {
      // For remove mode, only show dancers that are in ALL selected routines
      return availableDancers.filter(dancer => 
        dancersInAllRoutines.has(dancer.dancerId)
      );
    }
  }, [availableDancers, mode, dancersInAllRoutines]);

  const handleDancerToggle = (dancerId) => {
    setSelectedDancers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dancerId)) {
        newSet.delete(dancerId);
      } else {
        newSet.add(dancerId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (selectedDancers.size === 0) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const updates = selectedRoutines.map(async (routine) => {
        const currentDancers = routine.dancers || [];
        let updatedDancers;
        
        if (mode === "add") {
          // Add selected dancers (avoid duplicates)
          updatedDancers = [...new Set([...currentDancers, ...selectedDancers])];
        } else {
          // Remove selected dancers
          updatedDancers = currentDancers.filter(id => !selectedDancers.has(id));
        }
        
        // Update routine via API
        const response = await fetchWithAuth(`${ROUTINES_API}/${routine.routineId}`, {
          method: "PUT",
          body: JSON.stringify({
            ...routine,
            dancers: updatedDancers,
          }),
        });
        
        return response;
      });
      
      await Promise.all(updates);
      onSuccess();
      
    } catch (err) {
      console.error("Error updating routines:", err);
      setError("Failed to update routines. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalTitle = () => {
    const routineCount = selectedRoutines.length;
    const dancerCount = selectedDancers.size;
    
    if (mode === "add") {
      return `Add Dancers to ${routineCount} Routine${routineCount !== 1 ? "s" : ""}`;
    } else {
      return `Remove Dancers from ${routineCount} Routine${routineCount !== 1 ? "s" : ""}`;
    }
  };

  const getSubmitButtonText = () => {
    const dancerCount = selectedDancers.size;
    if (mode === "add") {
      return `Add ${dancerCount} Dancer${dancerCount !== 1 ? "s" : ""}`;
    } else {
      return `Remove ${dancerCount} Dancer${dancerCount !== 1 ? "s" : ""}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className={`
          relative w-full max-w-4xl p-6 rounded-xl
          ${isDarkMode 
            ? "bg-[#1a1f2d] border border-white/10" 
            : "bg-white border border-base-300"}
        `}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`
                w-12 h-12 rounded-full flex items-center justify-center
                ${mode === "add" 
                  ? isDarkMode ? "bg-green-500/10" : "bg-green-100" 
                  : isDarkMode ? "bg-orange-500/10" : "bg-orange-100"
                }
              `}>
                {mode === "add" ? (
                  <Plus className={`w-6 h-6 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
                ) : (
                  <Minus className={`w-6 h-6 ${isDarkMode ? "text-orange-400" : "text-orange-600"}`} />
                )}
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>
                  {getModalTitle()}
                </h3>
                <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                  {mode === "add" 
                    ? "Select dancers to add to all selected routines"
                    : "Select dancers to remove from all selected routines"
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-white/10 text-gray-400"
                  : "hover:bg-gray-100 text-gray-500"
              }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Selected Routines Summary */}
          <div className={`mb-6 p-4 rounded-lg ${
            isDarkMode ? "bg-white/5" : "bg-gray-50"
          }`}>
            <h4 className={`font-medium mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Routines to Update ({selectedRoutines.length})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {selectedRoutines.map(routine => (
                <div key={routine.routineId} className={`
                  px-3 py-2 rounded-lg text-sm
                  ${isDarkMode ? "bg-white/5 text-gray-300" : "bg-white text-gray-700"}
                `}>
                  {routine.title}
                </div>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search dancers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pr-10 pl-4 py-3 rounded-lg ${
                  isDarkMode
                    ? "bg-[#1a1f2d] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    : "input input-bordered"
                }`}
              />
              <Search className={`absolute right-3 top-3 h-5 w-5 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`} />
            </div>
          </div>

          {/* Dancer Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Available Dancers ({filteredDancers.length})
              </h4>
              {selectedDancers.size > 0 && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  isDarkMode
                    ? "bg-blue-500/20 text-blue-300"
                    : "bg-blue-100 text-blue-700"
                }`}>
                  {selectedDancers.size} selected
                </span>
              )}
            </div>

            {/* Dancer List */}
            <div className={`max-h-64 overflow-y-auto rounded-lg border ${
              isDarkMode ? "border-white/10" : "border-gray-200"
            }`}>
              {filteredDancers.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className={`mx-auto h-12 w-12 mb-4 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`} />
                  <p className={`text-lg font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}>
                    {mode === "add" ? "No dancers found" : "No dancers in all selected routines"}
                  </p>
                  <p className={`text-sm ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}>
                    {mode === "add" 
                      ? "Try adjusting your search terms"
                      : "Dancers must be in all selected routines to be removed"
                    }
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredDancers.map((dancer) => {
                    const isSelected = selectedDancers.has(dancer.dancerId);
                    const age = dancer.dateOfBirth
                      ? new Date().getFullYear() - new Date(dancer.dateOfBirth).getFullYear()
                      : null;
                    
                    return (
                      <div
                        key={dancer.dancerId}
                        onClick={() => handleDancerToggle(dancer.dancerId)}
                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                          isSelected
                            ? isDarkMode
                              ? "bg-blue-500/20 border-l-4 border-blue-400"
                              : "bg-blue-50 border-l-4 border-blue-500"
                            : isDarkMode
                            ? "hover:bg-white/5 border-l-4 border-transparent"
                            : "hover:bg-gray-50 border-l-4 border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Avatar/Initials */}
                          {dancer.profilePicture ? (
                            <img
                              src={dancer.profilePicture}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                              isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700"
                            }`}>
                              {dancer.firstName?.[0]?.toUpperCase() || "?"}
                              {dancer.lastName?.[0]?.toUpperCase() || ""}
                            </div>
                          )}
                          
                          {/* Name and Age */}
                          <div>
                            <div className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}>
                              {dancer.firstName} {dancer.lastName}
                            </div>
                            {age && (
                              <div className={`text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}>
                                {age} years old
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Selection Indicator */}
                        <div className={`transition-opacity ${
                          isSelected ? "opacity-100" : "opacity-0"
                        }`}>
                          <Check className={`h-5 w-5 ${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Warning for Remove Mode */}
          {mode === "remove" && selectedDancers.size > 0 && (
            <div className={`mb-6 p-4 rounded-lg border ${
              isDarkMode ? "bg-orange-500/10 border-orange-500/20" : "bg-orange-50 border-orange-200"
            }`}>
              <div className="flex items-start gap-3">
                <AlertCircle className={`h-5 w-5 mt-0.5 ${
                  isDarkMode ? "text-orange-400" : "text-orange-600"
                }`} />
                <div>
                  <h5 className={`font-medium mb-1 ${
                    isDarkMode ? "text-orange-300" : "text-orange-800"
                  }`}>
                    Important Notice
                  </h5>
                  <p className={`text-sm ${
                    isDarkMode ? "text-orange-400" : "text-orange-700"
                  }`}>
                    Removing dancers from routines will affect routine registrations and may change routine categories. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className={`mb-6 p-4 rounded-lg border ${
              isDarkMode ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"
            }`}>
              <div className="flex items-center gap-3">
                <AlertCircle className={`h-5 w-5 ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`} />
                <span className={`text-sm ${
                  isDarkMode ? "text-red-400" : "text-red-700"
                }`}>
                  {error}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                isDarkMode
                  ? "bg-white/5 hover:bg-white/10 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || selectedDancers.size === 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isSubmitting || selectedDancers.size === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              } ${
                mode === "add"
                  ? isDarkMode
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                  : isDarkMode
                    ? "bg-orange-600 hover:bg-orange-700 text-white"
                    : "bg-orange-600 hover:bg-orange-700 text-white"
              }`}
            >
              {isSubmitting ? "Updating..." : getSubmitButtonText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDancerModal;
